import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i0 from '@angular/core';
import { signal, contentChild, effect, contentChildren, input, computed, Input, HostBinding, ContentChild, ViewChild, ChangeDetectionStrategy, Component, inject, Injector, afterRender, forwardRef, HostListener, NgModule } from '@angular/core';
import { NxErrorComponent } from '@aposin/ng-aquila/base';
import { Subject, merge } from 'rxjs';
import { startWith, takeUntil, throttleTime, delay } from 'rxjs/operators';
import * as i1 from '@angular/cdk/overlay';
import { OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NxDropdownComponent } from '@aposin/ng-aquila/dropdown';
import { NxFormfieldControl, NxFormfieldErrorDirective } from '@aposin/ng-aquila/formfield';
import * as i3 from '@aposin/ng-aquila/popover';
import { NxPopoverModule } from '@aposin/ng-aquila/popover';
import { getFontShorthand } from '@aposin/ng-aquila/utils';
import { CommonModule } from '@angular/common';
import { NxInputModule } from '@aposin/ng-aquila/input';
const _c0 = ["popover"];
const _c1 = [[["nx-dropdown"]], [["", "nxError", ""]], [["", "nxInput", ""]]];
const _c2 = ["nx-dropdown", "[nxError]", "[nxInput]"];
function NxWordComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵprojection(1, 2);
    i0.ɵɵelementEnd();
  }
}
const _c3 = ["*", [["nx-error"]]];
const _c4 = ["*", "nx-error"];
function NxNaturalLanguageFormComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵprojection(1, 1);
    i0.ɵɵelementEnd();
  }
}
class NaturalLanguageFormErrorWrapper {}
class NxWordComponent {
  static {
    this._labelKey = 0;
  }
  constructor(/** @docs-private */elementRef, _cdr, _renderer, _overlay, _viewContainerRef, _overlayPositionBuilder, _nlfErrorWrapper) {
    this.elementRef = elementRef;
    this._cdr = _cdr;
    this._renderer = _renderer;
    this._overlay = _overlay;
    this._viewContainerRef = _viewContainerRef;
    this._overlayPositionBuilder = _overlayPositionBuilder;
    this._nlfErrorWrapper = _nlfErrorWrapper;
    this.labelId = `nx-word-label-${NxWordComponent._labelKey++}`;
    /** @docs-private */
    this.inputChanges = new Subject();
    /** @docs-private */
    this._hasErrors = signal(false);
    this.control = contentChild.required(NxFormfieldControl);
    /**
     * @docs-private
     * We should not set `for` and also use `aria-labelledby` at the same time.
     * For some components that automatically render label and connect it to the input, this will cause issues otherwise.
     * This is a workaround for the issue that we cannot set `aria-labelledby` on the input directly and to not introduce breaking changes.
     */
    this._setAriaLabelledBy = effect(() => {
      const control = this.control();
      // if aria-labelledby is set, add the word label to the list
      if (control.ariaLabelledBy !== null && control.ariaLabelledBy !== undefined) {
        control.ariaLabelledBy = `${this.labelId} ${control.ariaLabelledBy}`;
      }
      // if no aria-labelledby is set, we set it to the word label
      if (control.ariaLabelledBy === null) {
        control.ariaLabelledBy = this.labelId;
      }
    });
    this._errorChildren = contentChildren(NxFormfieldErrorDirective);
    /** @docs-private */
    this.currentTextWidth = 0;
    // this will apply different min-widths to our component through our styles
    /** Provide a hint for a minimal width. The actual size will be determined for inputs for each change. */
    this.size = 'regular';
    /**
     * A word doesn't have a set place to show labels.
     * In order to be accessible, you have to provide a label with this property.
     * It will be attached to the given input through `aria-label`.
     */
    this.label = '';
    /**
     * Sets the `aria-describedby` for the formfield.
     * Should be used to refer to the individual error message.
     *
     * If not set it will be set to the id of the entire error message or the error message inside the nx-word (deprecated)
     *
     * Should be space seperated list of `id`s.
     */
    this.describedByInput = input(undefined, {
      alias: 'describedBy'
    });
    this.describedBy = computed(() => {
      // fallback to nlfError if describedBy is not set
      const useNaturalLanguageFormDescribedBy = this.describedByInput() === undefined && this._nlfErrorWrapper._errorId() !== undefined;
      const nlfError = useNaturalLanguageFormDescribedBy ? this._nlfErrorWrapper._errorId() : undefined;
      // fallback to the error children inside nx-word that is shown in the popover (deprecated)
      const errorChildren = this._errorChildren()?.map(error => error.id);
      // ignore any undefined and null values
      return [nlfError, ...errorChildren, this.describedByInput()].filter(id => id != null);
    });
    this.describedByEffect = effect(() => {
      this.control().setDescribedByIds(this.describedBy());
    });
    this._destroyed = new Subject();
    this._updatePopoverStateEffect = effect(() => {
      this._errorChildren();
      this.updateErrorPopoverState();
    });
  }
  ngOnInit() {
    this.setupErrorPopover();
  }
  ngAfterContentInit() {
    this.control().stateChanges.pipe(startWith(null), takeUntil(this._destroyed)).subscribe(() => {
      this._hasErrors.set(this.control().errorState);
      this.updateErrorPopoverState();
      this._cdr.markForCheck();
    });
    this.control().stateChanges.pipe(takeUntil(this._destroyed)).subscribe(value => {
      this.updateCurrentTextWidth();
      this.inputChanges.next();
    });
  }
  ngOnDestroy() {
    this._updatePopoverStateEffect.destroy();
    this._setAriaLabelledBy.destroy();
    this._destroyed.next();
    this._destroyed.complete();
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }
  /**
   * Calculate the width of the full text given by the input, that value is bound to this component so it can grow.
   *
   * The involved input is known to have a width of 100% an will fit the additional space given which completes the auto growing behavior.
   * @docs-private
   */
  updateCurrentTextWidth() {
    // provide the canvas only lazy
    if (!this.measureCanvas) {
      this.measureCanvas = this._renderer.createElement('canvas');
    }
    const ctx = this.measureCanvas.getContext('2d');
    if (!ctx) {
      return;
    }
    const inputRef = this.control().elementRef;
    const styles = window.getComputedStyle(inputRef.nativeElement);
    ctx.font = getFontShorthand(styles);
    const metrics = ctx.measureText(inputRef.nativeElement.value);
    // add 1px (cursor width) to prevent jumping of the text on blur.
    const newWidth = metrics.width + parseInt(styles.paddingRight, 10) + parseInt(styles.paddingLeft, 10) + 1;
    // This should be injected via @Host to get an exact reference to NxNaturalLanguageFormComponent
    // Works as promised as long as there is not other tag around the word. Not expected but possible.
    const parent = this.elementRef.nativeElement.parentElement;
    const parentMeasurement = parent.getBoundingClientRect();
    // Limit to own given minimal width
    this.currentTextWidth = Math.max(parseInt(styles.minWidth, 10), newWidth);
    // Limit to container width
    this.currentTextWidth = Math.min(this.currentTextWidth, parentMeasurement.width);
    if (this._overlayRef?.hasAttached()) {
      this._overlayState.positionStrategy.apply();
    }
  }
  /** @docs-private */
  repositionError() {
    if (this._overlayRef?.hasAttached()) {
      this._overlayState.positionStrategy.apply();
    }
  }
  /** @docs-private */
  getConnectedOverlayOrigin() {
    return this.elementRef;
  }
  /** @docs-private */
  get isFocused() {
    return this.control().focused;
  }
  /** @docs-private */
  get isFilled() {
    return !this.control().empty;
  }
  /** @docs-private */
  get hasDropdown() {
    return Boolean(this._dropdown);
  }
  updateErrorPopoverState() {
    if (this._hasErrors() && this._errorChildren().length > 0) {
      this.showPopover();
    } else {
      this.hidePopover();
    }
  }
  setupErrorPopover() {
    // error popovers should not be focusable because they will be read via aria-describedby
    this._popover.triggerType = 'manual';
    this._popover.tabIndex = null;
    const positionStrategy = this._overlayPositionBuilder.flexibleConnectedTo(this.elementRef).withLockedPosition(true).withFlexibleDimensions(false).withPush(true).withPositions([{
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom'
    }, {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top'
    }]).withDefaultOffsetY(-8);
    this._overlayState = new OverlayConfig();
    this._overlayState.positionStrategy = positionStrategy;
    this._overlayState.scrollStrategy = this._overlay.scrollStrategies.reposition();
    this._overlayRef = this._overlay.create(this._overlayState);
    this._overlayState.positionStrategy.positionChanges.pipe(takeUntil(this._destroyed)).subscribe(change => {
      const pair = change.connectionPair;
      this.positionArrow(pair);
      // These position changes arrive too late,
      // We have to trigger the change detection manually
      // as it's detached from any render hierarchy
      // and only updated by the overlay when attached.
      if (this._embeddedViewRef && !this._embeddedViewRef.destroyed) {
        this._embeddedViewRef.detectChanges();
      }
    });
  }
  positionArrow(pair) {
    if (this._overlayRef) {
      const parentElementPositionX = this.elementRef.nativeElement.getBoundingClientRect().left;
      const parentElementWidth = this.elementRef.nativeElement.getBoundingClientRect().width / 2;
      const parentElementLeftOffset = this._overlayRef.overlayElement.parentElement.offsetLeft;
      const overlayElementLeftOffset = this._overlayRef.overlayElement.offsetLeft;
      // calculation for x position of the parent element. In this case, overlay left offset is the one thing to consider.
      const targetPosition = parentElementPositionX + parentElementWidth - (parentElementLeftOffset + overlayElementLeftOffset);
      if (pair.originY === 'top' && pair.overlayY === 'bottom') {
        this._popover.direction = 'top';
      } else {
        this._popover.direction = 'bottom';
      }
      this._popover.arrowStyle = {
        left: targetPosition + 'px'
      };
    }
  }
  showPopover() {
    if (!this._overlayRef?.hasAttached()) {
      const tooltipPortal = new TemplatePortal(this._popover.templateRef, this._viewContainerRef);
      this._embeddedViewRef = this._overlayRef?.attach(tooltipPortal);
    }
  }
  hidePopover() {
    this._overlayRef?.detach();
  }
  static {
    this.ɵfac = function NxWordComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxWordComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i1.Overlay), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i1.OverlayPositionBuilder), i0.ɵɵdirectiveInject(NaturalLanguageFormErrorWrapper));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxWordComponent,
      selectors: [["nx-word"]],
      contentQueries: function NxWordComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuerySignal(dirIndex, ctx.control, NxFormfieldControl, 5);
          i0.ɵɵcontentQuerySignal(dirIndex, ctx._errorChildren, NxFormfieldErrorDirective, 4);
          i0.ɵɵcontentQuery(dirIndex, NxFormfieldControl, 5);
          i0.ɵɵcontentQuery(dirIndex, NxDropdownComponent, 5);
        }
        if (rf & 2) {
          i0.ɵɵqueryAdvance(2);
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._control = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._dropdown = _t.first);
        }
      },
      viewQuery: function NxWordComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 7);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._popover = _t.first);
        }
      },
      hostVars: 16,
      hostBindings: function NxWordComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵstyleProp("width", ctx.currentTextWidth, "px");
          i0.ɵɵclassProp("size-short", ctx.size == "short")("size-regular", ctx.size == "regular")("size-long", ctx.size == "long")("has-error", ctx._hasErrors())("is-focused", ctx.isFocused)("is-filled", ctx.isFilled)("has-dropdown", ctx.hasDropdown);
        }
      },
      inputs: {
        size: "size",
        label: "label",
        describedByInput: [1, "describedBy", "describedByInput"]
      },
      ngContentSelectors: _c2,
      decls: 8,
      vars: 4,
      consts: [["popover", ""], [1, "cdk-visually-hidden"], [1, "nx-word__inner-wrapper"]],
      template: function NxWordComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c1);
          i0.ɵɵelementStart(0, "div")(1, "label", 1);
          i0.ɵɵtext(2);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(3, NxWordComponent_Conditional_3_Template, 2, 0, "div", 2);
          i0.ɵɵprojection(4);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(5, "nx-popover", null, 0);
          i0.ɵɵprojection(7, 1);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵadvance();
          i0.ɵɵattribute("for", ctx.hasDropdown ? "" : ctx.control().id)("id", ctx.labelId);
          i0.ɵɵadvance();
          i0.ɵɵtextInterpolate(ctx.label);
          i0.ɵɵadvance();
          i0.ɵɵconditional(!ctx.hasDropdown ? 3 : -1);
        }
      },
      dependencies: [NxPopoverModule, i3.NxPopoverComponent],
      styles: ["[_nghost-%COMP%]{display:inline-block;position:relative;font-weight:400;text-align:center;hyphens:none;width:100%;margin:0 4px}[_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%]{border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:var(--natural-language-form-border-color)}.size-short[_nghost-%COMP%]{min-width:80px}.size-regular[_nghost-%COMP%]{min-width:152px}.size-long[_nghost-%COMP%]{min-width:360px}@media (max-width: 991px){.size-long[_nghost-%COMP%]{min-width:280px}}@media (max-width: 703px){.size-long[_nghost-%COMP%]{min-width:248px}}[_nghost-%COMP%]     .c-input{display:inline-block;text-align:inherit;font-size:inherit;line-height:inherit;background:0 0;color:inherit;border:none;outline:0;margin:0;width:100%;max-width:100%;height:auto;box-shadow:none;padding:0 8px}[_nghost-%COMP%]     .c-input.is-focused{outline:none;box-shadow:none}[_nghost-%COMP%]     .c-input.is-filled, [_nghost-%COMP%]     .c-input.is-focused{color:var(--natural-language-form-active-color);font-weight:400}[_nghost-%COMP%]     .c-input.is-disabled{cursor:not-allowed}[_nghost-%COMP%]     .c-input::-ms-clear{display:none}[_nghost-%COMP%]     nx-dropdown{position:relative;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:var(--natural-language-form-border-color)}[_nghost-%COMP%]     nx-dropdown .nx-dropdown__container{font-size:inherit;margin-bottom:-nx-spacer(4xs)}@media (max-width: 991px){[_nghost-%COMP%]     nx-dropdown .nx-dropdown__container{font-size:26px}}[_nghost-%COMP%]     nx-dropdown.is-filled, [_nghost-%COMP%]     nx-dropdown.has-focus{color:var(--natural-language-form-active-color);border-color:var(--natural-language-form-active-border-color)}[_nghost-%COMP%]     nx-dropdown.has-focus{border-bottom-width:4px}[_nghost-%COMP%]     nx-dropdown .nx-dropdown__icon{line-height:inherit}[_nghost-%COMP%]     .nx-word__input-wrapper{width:100%}.is-focused[_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%], .is-filled[_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%]{border-color:var(--natural-language-form-active-border-color)}.is-focused[_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%]{border-bottom-width:4px}.has-error[_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%]{border-color:var(--natural-language-form-error-border-color)}.has-error[_nghost-%COMP%]     nx-dropdown{color:var(--natural-language-form-error-color);border-color:var(--natural-language-form-error-border-color)}.has-error[_nghost-%COMP%]     .c-input.has-error{color:var(--natural-language-form-error-color)}.is-negative[_nghost-%COMP%]     .nx-dropdown, .is-negative   [_nghost-%COMP%]     .nx-dropdown, .is-negative[_nghost-%COMP%]     nx-dropdown, .is-negative   [_nghost-%COMP%]     nx-dropdown, .is-negative[_nghost-%COMP%]     nx-dropdown.is-filled, .is-negative   [_nghost-%COMP%]     nx-dropdown.is-filled{border-color:var(--negative);color:var(--negative)}.is-negative[_nghost-%COMP%]     .c-input, .is-negative   [_nghost-%COMP%]     .c-input, .is-negative[_nghost-%COMP%]     .c-input.has-error, .is-negative   [_nghost-%COMP%]     .c-input.has-error{color:var(--negative)}.is-negative[_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%], .is-negative   [_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%]{border-color:var(--negative)}.nx-word__inner-wrapper[_ngcontent-%COMP%]{height:var(--natural-language-form-large-line-height)}@media (max-width: 991px){.nx-word__inner-wrapper[_ngcontent-%COMP%]{height:var(--natural-language-form-small-line-height)}}.nx-natural-language-form--small[_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%], .nx-natural-language-form--small   [_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%]{height:var(--natural-language-form-small-line-height)}@media screen and (forced-colors: active){.nx-word__inner-wrapper[_ngcontent-%COMP%],   nx-dropdown{border-bottom-color:buttonText!important}.is-focused[_nghost-%COMP%]   .nx-word__inner-wrapper[_ngcontent-%COMP%], .is-focused[_nghost-%COMP%]     nx-dropdown{border-bottom-color:highlight!important}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxWordComponent, [{
    type: Component,
    args: [{
      selector: 'nx-word',
      host: {
        '[class.size-short]': 'size == "short"',
        '[class.size-regular]': 'size == "regular"',
        '[class.size-long]': 'size == "long"',
        '[class.has-error]': '_hasErrors()',
        '[class.is-focused]': 'isFocused',
        '[class.is-filled]': 'isFilled',
        '[class.has-dropdown]': 'hasDropdown'
      },
      changeDetection: ChangeDetectionStrategy.OnPush,
      imports: [NxPopoverModule],
      template: "<div>\n    <label class=\"cdk-visually-hidden\" [attr.for]=\"hasDropdown ? '' : control().id\" [attr.id]=\"labelId\">{{ label }}</label>\n    <!-- We only allow formfield controls here -->\n    @if (!hasDropdown) {\n        <div class=\"nx-word__inner-wrapper\">\n            <ng-content select=\"[nxInput]\"></ng-content>\n        </div>\n    }\n    <ng-content select=\"nx-dropdown\"></ng-content>\n</div>\n\n<!-- deprecated: please refer to natural language form examples for improved and more user friendly error messages -->\n<nx-popover #popover>\n    <ng-content select=\"[nxError]\"></ng-content>\n</nx-popover>\n",
      styles: [":host{display:inline-block;position:relative;font-weight:400;text-align:center;hyphens:none;width:100%;margin:0 4px}:host .nx-word__inner-wrapper{border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:var(--natural-language-form-border-color)}:host.size-short{min-width:80px}:host.size-regular{min-width:152px}:host.size-long{min-width:360px}@media (max-width: 991px){:host.size-long{min-width:280px}}@media (max-width: 703px){:host.size-long{min-width:248px}}:host ::ng-deep .c-input{display:inline-block;text-align:inherit;font-size:inherit;line-height:inherit;background:0 0;color:inherit;border:none;outline:0;margin:0;width:100%;max-width:100%;height:auto;box-shadow:none;padding:0 8px}:host ::ng-deep .c-input.is-focused{outline:none;box-shadow:none}:host ::ng-deep .c-input.is-filled,:host ::ng-deep .c-input.is-focused{color:var(--natural-language-form-active-color);font-weight:400}:host ::ng-deep .c-input.is-disabled{cursor:not-allowed}:host ::ng-deep .c-input::-ms-clear{display:none}:host ::ng-deep nx-dropdown{position:relative;border-bottom-width:2px;border-bottom-style:solid;border-bottom-color:var(--natural-language-form-border-color)}:host ::ng-deep nx-dropdown .nx-dropdown__container{font-size:inherit;margin-bottom:-nx-spacer(4xs)}@media (max-width: 991px){:host ::ng-deep nx-dropdown .nx-dropdown__container{font-size:26px}}:host ::ng-deep nx-dropdown.is-filled,:host ::ng-deep nx-dropdown.has-focus{color:var(--natural-language-form-active-color);border-color:var(--natural-language-form-active-border-color)}:host ::ng-deep nx-dropdown.has-focus{border-bottom-width:4px}:host ::ng-deep nx-dropdown .nx-dropdown__icon{line-height:inherit}:host ::ng-deep .nx-word__input-wrapper{width:100%}:host(.is-focused) .nx-word__inner-wrapper,:host(.is-filled) .nx-word__inner-wrapper{border-color:var(--natural-language-form-active-border-color)}:host(.is-focused) .nx-word__inner-wrapper{border-bottom-width:4px}:host(.has-error) .nx-word__inner-wrapper{border-color:var(--natural-language-form-error-border-color)}:host.has-error ::ng-deep nx-dropdown{color:var(--natural-language-form-error-color);border-color:var(--natural-language-form-error-border-color)}:host.has-error ::ng-deep .c-input.has-error{color:var(--natural-language-form-error-color)}:host-context(.is-negative) ::ng-deep .nx-dropdown,:host-context(.is-negative) ::ng-deep nx-dropdown,:host-context(.is-negative) ::ng-deep nx-dropdown.is-filled{border-color:var(--negative);color:var(--negative)}:host-context(.is-negative) ::ng-deep .c-input,:host-context(.is-negative) ::ng-deep .c-input.has-error{color:var(--negative)}:host-context(.is-negative) .nx-word__inner-wrapper{border-color:var(--negative)}.nx-word__inner-wrapper{height:var(--natural-language-form-large-line-height)}@media (max-width: 991px){.nx-word__inner-wrapper{height:var(--natural-language-form-small-line-height)}}:host-context(.nx-natural-language-form--small) .nx-word__inner-wrapper{height:var(--natural-language-form-small-line-height)}@media screen and (forced-colors: active){.nx-word__inner-wrapper,::ng-deep nx-dropdown{border-bottom-color:buttonText!important}:host(.is-focused) .nx-word__inner-wrapper,:host(.is-focused) ::ng-deep nx-dropdown{border-bottom-color:highlight!important}}\n"]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: i0.Renderer2
  }, {
    type: i1.Overlay
  }, {
    type: i0.ViewContainerRef
  }, {
    type: i1.OverlayPositionBuilder
  }, {
    type: NaturalLanguageFormErrorWrapper
  }], {
    _control: [{
      type: ContentChild,
      args: [NxFormfieldControl]
    }],
    _popover: [{
      type: ViewChild,
      args: ['popover', {
        static: true
      }]
    }],
    _dropdown: [{
      type: ContentChild,
      args: [NxDropdownComponent]
    }],
    currentTextWidth: [{
      type: HostBinding,
      args: ['style.width.px']
    }],
    size: [{
      type: Input
    }],
    label: [{
      type: Input,
      args: ['label']
    }]
  });
})();
const DEFAULT_SIZE = 'large';
class NxNaturalLanguageFormComponent {
  /** Whether the negative set of stylings should be used. */
  set negative(value) {
    this._negative = coerceBooleanProperty(value);
  }
  get negative() {
    return this._negative;
  }
  /**
   * Sets the size of the NLF. Default value: large.
   */
  set size(value) {
    this._size = value;
    this._cdr.markForCheck();
  }
  get size() {
    return this._size;
  }
  constructor(_cdr) {
    this._cdr = _cdr;
    this._negative = false;
    this._injector = inject(Injector);
    /** @docs-private */
    this._size = DEFAULT_SIZE;
    /** @docs-private */
    this.resizeEvent$ = new Subject();
    this._errorComponent = contentChild(NxErrorComponent);
    /**
     * @docs-private
     * The id of the error component. This is referenced by the word components in case of an error and no provided describedBy value
     */
    this._errorId = computed(() => this._errorComponent()?.id);
    /**
     * All nx-word content children.
     */
    this._wordsComponents = contentChildren(NxWordComponent);
    /**
     * Readable Signal of collective nx-word error states
     * @returns `true` if at least one word has an error. `false` if no word as an error.
     */
    this.wordsErrorStates = computed(() => {
      if (!this._wordsComponents()) {
        return false;
      }
      return this._wordsComponents().some(word => word._hasErrors());
    });
    this._destroyed = new Subject();
    afterRender({
      write: () => {
        this.updatePositionPopovers();
      }
    }, {
      injector: this._injector
    });
  }
  /** @docs-private */
  onResize(event) {
    this.resizeEvent$.next();
  }
  ngAfterContentInit() {
    // Collect all words and listen for changes so we can update any open error popover
    // which would otherwise get wrongly positioned.
    const subjects = this._wordsComponents().map(word => word.inputChanges);
    const source = merge(...subjects);
    source.pipe(takeUntil(this._destroyed)).subscribe(() => {
      this.updatePositionPopovers();
    });
    this.resizeObservable = this.resizeEvent$.pipe(throttleTime(500), delay(100));
    this.resizeObservable.pipe(takeUntil(this._destroyed)).subscribe(() => this.resizeWords());
  }
  /** @docs-private */
  resizeWords() {
    this._wordsComponents().forEach(word => {
      word.updateCurrentTextWidth();
    });
  }
  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }
  /** @docs-private */
  updatePositionPopovers() {
    if (this._wordsComponents()) {
      this._wordsComponents().forEach(word => {
        word.repositionError();
      });
    }
  }
  static {
    this.ɵfac = function NxNaturalLanguageFormComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxNaturalLanguageFormComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxNaturalLanguageFormComponent,
      selectors: [["nx-natural-language-form"]],
      contentQueries: function NxNaturalLanguageFormComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuerySignal(dirIndex, ctx._errorComponent, NxErrorComponent, 5);
          i0.ɵɵcontentQuerySignal(dirIndex, ctx._wordsComponents, NxWordComponent, 4);
        }
        if (rf & 2) {
          i0.ɵɵqueryAdvance(2);
        }
      },
      hostVars: 6,
      hostBindings: function NxNaturalLanguageFormComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("orientationchange", function NxNaturalLanguageFormComponent_orientationchange_HostBindingHandler($event) {
            return ctx.onResize($event);
          }, false, i0.ɵɵresolveWindow)("resize", function NxNaturalLanguageFormComponent_resize_HostBindingHandler($event) {
            return ctx.onResize($event);
          }, false, i0.ɵɵresolveWindow);
        }
        if (rf & 2) {
          i0.ɵɵclassProp("is-negative", ctx.negative)("nx-natural-language-form--small", ctx.size === "small")("nx-natural-language-form--large", ctx.size === "large");
        }
      },
      inputs: {
        negative: [0, "negativeStyles", "negative"],
        size: "size"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NaturalLanguageFormErrorWrapper,
        useExisting: forwardRef(() => NxNaturalLanguageFormComponent)
      }])],
      ngContentSelectors: _c4,
      decls: 3,
      vars: 1,
      consts: [[1, "nx-natural-language-form__wrapper"], [1, "nx-natural-language-form__error-wrapper"]],
      template: function NxNaturalLanguageFormComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c3);
          i0.ɵɵelementStart(0, "div", 0);
          i0.ɵɵprojection(1);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(2, NxNaturalLanguageFormComponent_Conditional_2_Template, 2, 0, "div", 1);
        }
        if (rf & 2) {
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.wordsErrorStates() ? 2 : -1);
        }
      },
      styles: ["[_nghost-%COMP%]{display:block;font-size:var(--natural-language-form-large-font-size);line-height:var(--natural-language-form-large-line-height);font-weight:var(--natural-language-form-large-font-weight);letter-spacing:var(--natural-language-form-large-letter-spacing);color:var(--natural-language-form-text-color);padding-top:72px;padding-bottom:80px}[_nghost-%COMP%]     .c-input{font-size:inherit;line-height:inherit}[_nghost-%COMP%]     nx-word+nx-word{margin-left:2px}[dir=rtl]   [_nghost-%COMP%]     nx-word+nx-word{margin-right:2px;margin-left:initial}.is-negative[_nghost-%COMP%]{color:var(--negative)}@media (max-width: 991px){[_nghost-%COMP%]{font-size:var(--natural-language-form-small-font-size);line-height:var(--natural-language-form-small-line-height);font-weight:var(--natural-language-form-small-font-weight);letter-spacing:var(--natural-language-form-small-letter-spacing)}}.nx-natural-language-form__wrapper[_ngcontent-%COMP%]{display:flex;flex-wrap:wrap}.nx-natural-language-form__wrapper[_ngcontent-%COMP%] >   *{margin-bottom:8px}.nx-natural-language-form--small[_nghost-%COMP%]{font-size:var(--natural-language-form-small-font-size);line-height:var(--natural-language-form-small-line-height);font-weight:var(--natural-language-form-small-font-weight);letter-spacing:var(--natural-language-form-small-letter-spacing)}.nx-natural-language-form--small[_nghost-%COMP%]     .c-input{font-size:inherit;line-height:inherit}.nx-natural-language-form__error-wrapper[_ngcontent-%COMP%]{margin-top:24px}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxNaturalLanguageFormComponent, [{
    type: Component,
    args: [{
      selector: 'nx-natural-language-form',
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        '[class.is-negative]': 'negative',
        '[class.nx-natural-language-form--small]': 'size === "small"',
        '[class.nx-natural-language-form--large]': 'size === "large"'
      },
      providers: [{
        provide: NaturalLanguageFormErrorWrapper,
        useExisting: forwardRef(() => NxNaturalLanguageFormComponent)
      }],
      standalone: true,
      template: "<div class=\"nx-natural-language-form__wrapper\">\n    <ng-content></ng-content>\n</div>\n\n@if (wordsErrorStates()) {\n    <div class=\"nx-natural-language-form__error-wrapper\">\n        <ng-content select=\"nx-error\"></ng-content>\n    </div>\n}\n",
      styles: [":host{display:block;font-size:var(--natural-language-form-large-font-size);line-height:var(--natural-language-form-large-line-height);font-weight:var(--natural-language-form-large-font-weight);letter-spacing:var(--natural-language-form-large-letter-spacing);color:var(--natural-language-form-text-color);padding-top:72px;padding-bottom:80px}:host ::ng-deep .c-input{font-size:inherit;line-height:inherit}:host ::ng-deep nx-word+nx-word{margin-left:2px}[dir=rtl] :host ::ng-deep nx-word+nx-word{margin-right:2px;margin-left:initial}:host.is-negative{color:var(--negative)}@media (max-width: 991px){:host{font-size:var(--natural-language-form-small-font-size);line-height:var(--natural-language-form-small-line-height);font-weight:var(--natural-language-form-small-font-weight);letter-spacing:var(--natural-language-form-small-letter-spacing)}}.nx-natural-language-form__wrapper{display:flex;flex-wrap:wrap}.nx-natural-language-form__wrapper>::ng-deep *{margin-bottom:8px}:host(.nx-natural-language-form--small){font-size:var(--natural-language-form-small-font-size);line-height:var(--natural-language-form-small-line-height);font-weight:var(--natural-language-form-small-font-weight);letter-spacing:var(--natural-language-form-small-letter-spacing)}:host(.nx-natural-language-form--small) ::ng-deep .c-input{font-size:inherit;line-height:inherit}.nx-natural-language-form__error-wrapper{margin-top:24px}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }], {
    negative: [{
      type: Input,
      args: ['negativeStyles']
    }],
    size: [{
      type: Input
    }],
    onResize: [{
      type: HostListener,
      args: ['window:orientationchange', ['$event']]
    }, {
      type: HostListener,
      args: ['window:resize', ['$event']]
    }]
  });
})();
class NxNaturalLanguageFormModule {
  static {
    this.ɵfac = function NxNaturalLanguageFormModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxNaturalLanguageFormModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxNaturalLanguageFormModule,
      imports: [CommonModule, NxInputModule, NxPopoverModule, NxNaturalLanguageFormComponent, NxWordComponent, NxWordComponent],
      exports: [NxNaturalLanguageFormComponent, NxWordComponent]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, NxInputModule, NxPopoverModule, NxWordComponent, NxWordComponent]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxNaturalLanguageFormModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, NxInputModule, NxPopoverModule, NxNaturalLanguageFormComponent, NxWordComponent, NxWordComponent],
      exports: [NxNaturalLanguageFormComponent, NxWordComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NxNaturalLanguageFormComponent, NxNaturalLanguageFormModule, NxWordComponent };
